import { gql, GraphQLClient } from "graphql-request";
import useSWR, { SWRConfig } from "swr";
import { useMemo, useCallback } from "react";
import { useDataChangeNotifier } from "@/hooks/useDataChangeNotifier";
import type { TAPIPlace, TAPISlide, TAPITeamAdmin, TAPITeamResponse, TAPITeamsResponse } from "./graphql-functions/types-graphql-functions";

export type { TAPIPlace, TAPISlide, TAPITeam } from "./graphql-functions/types-graphql-functions";

export { postWithAuth, postGraphqlMutation } from './graphql-functions/graphql-functions-common';
export { fetchAuthBySecretCode } from './graphql-functions/fetchAuthBySecretCode';
export { postCreatePlace } from './graphql-functions/postCreatePlace';
export { useSubscribeTeam } from './graphql-functions/postSubscribeTeam';
export type { SubscribeTeamInput, SubscribeTeamLevel, SFErrors } from './graphql-functions/postSubscribeTeam';
export { postSubscribe } from './graphql-functions/postSubscribe';
export type { SubscribeInput } from './graphql-functions/postSubscribe';
export { postCreateTeam } from './graphql-functions/postCreateTeam';
export { postDeleteTeam } from './graphql-functions/postDeleteTeam';
export { postDeletePlace } from './graphql-functions/postDeletePlace';
export { postInviteMember } from './graphql-functions/postInviteMember';
export { useMe } from '../hooks/useMe';
export type { UserMe } from '../hooks/useMe';


/**
 * Provider component that sets up GraphQL client and SWR configuration
 * for the application.
 * 
 * GraphQL is used for all data retrieval. Mutations are not currently supported.
 * SWR is used for caching and revalidation of fetched data.
 * 
 * This is heavily inspired by:
 * - https://graphql.wtf/episodes/58-graphql-queries-and-mutations-with-react-swr
 */
export function GraphQLAndSWRAPIsProvider({ children }: { children: React.ReactNode }) {
    const client = useMemo(() => {
        // Create absolute URL from relative path to work with any host and port
        // window.location.host includes port number if present (e.g., "localhost:3000")
        const baseUrl = typeof window !== 'undefined' 
            ? `${window.location.protocol}//${window.location.host}` 
            : '';
        const endpoint = `${baseUrl}/api/graphql`;
        return new GraphQLClient(endpoint);
    }, []);
    
    const fetcher = useCallback(async (args:[string, { variables?: {[id: string]:any} }]) => {
        const [query, variables] = args;
        console.log("GraphQL fetcher called:", [query, variables]);
        try {
          const ret = await client.request(gql`${query}`, variables);
          console.log("GraphQL fetcher result:", ret);
          return ret;
        } catch (error) {
          console.error("GraphQL fetcher error:", error);
          throw error;
        }
    }, [client]);
    
    const swrConfig = useMemo(() => ({
        fetcher,
        revalidateOnFocus: false,
        shouldRetryOnError: false,
    }), [fetcher]);
    
    return (
        <SWRConfig value={swrConfig}>
            {children}
        </SWRConfig>
    );
}


/**
 * Use SWR to fetch place details by ID via GraphQL.
 * Includes the latest slide thumbnail/image URL.
 * @param placeId ID of the place to fetch details for
 * @returns SWR response with place details data and mutate function to refresh
 */
export function useGQL_placeDetailsById(placeId?: string) {
    const result = useSWR<{place: TAPIPlace}, any>(placeId ? [`query ($placeId: String!) {
        place(id: $placeId) {
            id
            name
            address
            place_code
            liveUrl
            latestSlide {
                id
                timestamp
                type
                text
                thumbnailUrl
                imageUrl
            }
        }
    }`, { placeId }] : null);
    
    useDataChangeNotifier([`place:${placeId}`, `slides:${placeId}`], () => { result.mutate(); });
    
    return result;
}

/**
 * Use SWR to fetch place details with full slide list via GraphQL.
 * @param placeId ID of the place to fetch slide details for
 * @returns SWR response with place details including slides array
 */
export function useGQL_placeSlideDetailsById(placeId?: string) {
    const result = useSWR<{place: TAPIPlace}, any>(placeId ? [`query ($placeId: String!) {
        place(id: $placeId) {
            id
            name
            address
            place_code
            liveUrl
            slides {
                id
                timestamp
                type
                text
                imageUrl
            }
        }
    }`, { placeId }] : null);
    
    useDataChangeNotifier([`place:${placeId}`, `slides:${placeId}`], () => { result.mutate(); });
    
    return result;
}


/**
 * Use SWR to fetch all available subscription plans from the server.
 * Plans are static (no user-specific data), so they are fetched once with no revalidation.
 */
export function useGQL_subscriptionPlans() {
    return useSWR<{ subscriptionPlans: Array<{ id: string; name: string; frequency: string; description: string | null }> }, any>([`query {
        subscriptionPlans {
            id
            name
            frequency
            description
        }
    }`, {}], { revalidateOnFocus: false, revalidateIfStale: false });
}


/**
 * Use SWR to fetch all teams with their places via GraphQL.
 * @returns SWR response with teams data
 */
export function useGQL_teams(options:{withPlaceStatus?: boolean}|undefined) {
    const result = useSWR<TAPITeamsResponse, any>([`query {
        teams {
            id
            name
            handle
            description
            deleted
            created_at
            places {
                id
                name
                ${options?.withPlaceStatus ? 'encoderOnline' : ''}
            }
        }
    }`, {}]);
    useDataChangeNotifier(['teams'], () => { result.mutate(); });
    return result;
}


/**
 * Use SWR to fetch the places for a single team via GraphQL.
 * Mirrors the `places` field/structure returned by `useGQL_teams`.
 * @param teamId Team to fetch places for
 * @param options.withPlaceStatus Include live `encoderOnline` status for each place
 */
export function usePlacesForTeam(teamId: string | undefined, options?: { withPlaceStatus?: boolean }) {
    const result = useSWR<{ team: { id: string; places: TAPIPlace[] } | null }, any>(teamId ? [`query ($teamId: String!) {
        team(id: $teamId) {
            id
            places {
                id
                name
                place_code
                ${options?.withPlaceStatus ? 'encoderOnline' : ''}
            }
        }
    }`, { teamId }] : null);
    useDataChangeNotifier(['teams', `team:${teamId}`], () => { result.mutate(); });
    return {
        places: result.data?.team?.places ?? [],
        isLoading: teamId != null && !result.data && !result.error,
        error: result.error,
    };
}

/**
 * Use SWR to fetch all teams with their places via GraphQL.
 * @returns SWR response with teams data
 */
export function useGQL_team(teamId: string | undefined, options?: { publicOnly?: boolean }) {
    const result = useSWR<TAPITeamResponse, any>([`query team($teamId: String!) {
        team(id: $teamId) {
            id
            name
            handle
            description
            deleted
            created_at
            subscriptionInfo {
                subPlanId
                subPlanName
                subPlanFrequency
                nextBillingDate
                screenCount
                isTrial
                sync
            }
            places${options?.publicOnly ? '(publicOnly: true)' : ''} {
                id
                name
                place_code
            }
        }
    }`, {teamId}]);
    useDataChangeNotifier(['teams', `team:${teamId}`], () => { result.mutate(); });
    return result;
}

type UseGQL_teamAdminsByIdGive = {
    teamInfo?: boolean;
    screens?: boolean;
    adminInfo?: boolean;
};

/**
 * Use SWR to fetch a single team's info by ID via GraphQL.
 * Pass `give` flags to opt into additional fields: teamInfo, screens, adminInfo.
 */
export function useGQL_teamAdminsById(teamId: string | undefined, give: UseGQL_teamAdminsByIdGive) {
    const query = useMemo(() => {
        const fields: string[] = [];
        if (give.teamInfo) fields.push('id', 'name', 'deleted', 'created_at');
        if (give.screens) fields.push('places { id name place_code }');
        if (give.adminInfo) fields.push('members { user { id email name } }');
        return `query ($teamId: String!) { team(id: $teamId) { ${fields.join('\n')} } }`;
    }, [give.teamInfo, give.screens, give.adminInfo]);
    const result = useSWR<{team: { id?: string; name?: string; deleted?: boolean; created_at?: string; admins?: TAPITeamAdmin[] }}, any>(
        teamId ? [query, { teamId }] : null
    );
    useDataChangeNotifier(['teams', `team:${teamId}`], () => { result.mutate(); });
    return {
        errors: result.error,
        team: result.data?.team,
        admins: result.data?.team?.members?.map(member => member.user) ?? [],
    };
}


/**
 * Use SWR to fetch a full place via GraphQL.
 * @returns SWR response with place data
 */
export function useGQL_placeById(placeId: string, give: { encoderKey: boolean|null}) {
    const query = useMemo(() => {
        let fields = [ 'id', 'name', 'address', 'latitude', 'longitude', 'googlePlaceId', 'place_code' ];
        if (give.encoderKey) {
            fields.push("encoderKey");
        }
        return `
            query ($placeId: String!) {
                place(id: $placeId) {
                    ${fields.join('\n')}
                }
            }
        `;
    }, [give]);
    const result = useSWR<{place: TAPIPlace}, any>([query, { placeId }]);
    useDataChangeNotifier([`place:${placeId}`], () => { result.mutate(); });
    return result;
}


/**
 * Use a raw query with the GraphQL client.
 * @param query GraphQL query string
 * @param variables Optional variables for the query
 */
export function useGQLClient(query: string, variables?: { [id: string]: any }) {
    return useSWR([query, variables ? { variables } : undefined]);
}

/**
 * Fetch a minimal list of teams (id + name only) for use in dropdowns.
 * Uses the standard teams query (proxied to remote accounts server).
 * Returns an empty array while loading or on error.
 */
export function useGQL_teamsSimple(options:{withoutSubscription?: boolean}|null): { teams: Array<{ id: string; name: string }>; isLoading: boolean } {
    const result = useSWR<{ teams: Array<{ id: string; name: string }> }, any>([`query teamsSimple($withoutSubscription: Boolean) {
        teams(withoutSubscription: $withoutSubscription) {
            id
            name
        }
    }`, {withoutSubscription: options?.withoutSubscription}]);
    return {
        teams: result.data?.teams ?? [],
        isLoading: !result.data && !result.error,
    };
}

export interface GQLMessage {
    id?: string;
    title?: string;
    markdown?: string;
    imageurl?: string;
    style?: string;
}

/**
 * Fetch messages for one or more `where` context keys in a single GraphQL request.
 * Each key is aliased in the query so results are keyed by `where` string.
 *
 * @param whereKeys Array of context key strings, e.g. `["today", "welcome"]`
 * @returns `{ messages: Record<string, GQLMessage[]>, fetching: boolean }`
 */
export function useGQL_messages(whereKeys: string[]): { messages: Record<string, GQLMessage[]>; fetching: boolean } {
    const query = useMemo(() => {
        if (whereKeys.length === 0) return null;
        const blocks = whereKeys
            .map(
                (key) =>
                    `${key}: messages(where: ${JSON.stringify(key)}) { id title markdown imageurl style }`,
            )
            .join('\n');
        return `query { ${blocks} }`;
    }, [whereKeys.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

    const result = useSWR<Record<string, GQLMessage[]>, any>(query ? [query, {}] : null);

    const messages = useMemo(() => result.data ?? {}, [result.data]);

    return {
        messages,
        fetching: query != null && !result.data && !result.error,
    };
}

/**
 * Poll a team's subscriptionInfo from the local TEAM_SUBSCRIPTION table until
 *
 * Refreshes every 2 seconds while subscriptionInfo.sync === true,
 * then stops automatically once resolved.
 *
 * @param teamId The team to watch — pass undefined/null until you have the ID.
 */
export function useGQL_teamSubscriptionStatus(teamId: string | undefined | null, refreshInterval?: number) {
    const POLL_QUERY = `query ($teamId: String!) { team(id: $teamId) { id subscriptionInfo { subPlanId isTrial sync } } }`;

    const result = useSWR<{ team: { id: string; subscriptionInfo: { subPlanId?: string | null; isTrial: boolean; sync?: boolean | null } | null } }, any>(
        teamId ? [POLL_QUERY, { teamId }] : null,
        {
            refreshInterval,
            // refreshInterval: (data) => {
            //     if (!data || !teamId) return 0;
            //     const team = data.teams?.find((t) => t.id === teamId);
            //     if (!team?.subscriptionInfo) return 2000; // still waiting for first record
            //     return team.subscriptionInfo.sync === true ? 2000 : 0;
            // },
        }
    );

    const team = result.data?.team;
    const subscriptionInfo = team?.subscriptionInfo ?? null;

    return {
        isLoading: teamId != null && !result.data && !result.error,
        /** True while the remote server is still confirming the subscription. */
        isSyncing: subscriptionInfo == null || subscriptionInfo.sync === true,
        subscriptionInfo,
    };
}
